import * as THREE from 'three/webgpu';
import { markOverlay } from '../render/assets/vramAccountant';
import type { EntityPlacement } from '../assets/handle';
import type { SpawnPlaceholder } from './librarySpawn';

/**
 * ── ⭐ P1.5·C · THE `entity` KIND'S PLACEHOLDER (plan §4.2's table, §13.6's "placeholder first") ──
 *
 * A summon runs in the GAMEPLAY lane, so its data arrives after the caller has already asked for
 * it; §13.6's requirement is *"placeholder first, real instance swapped in on ready"*. This is that
 * placeholder and it is deliberately the smallest honest one: a wireframe cage where the summoned
 * thing WILL be, disposed the instant the real instance lands.
 *
 * ⚠️ IT IS NOT MAGENTA, and that is a decision rather than a palette. Magenta is the MESH kind's
 * placeholder — "this asset did not arrive" — and a summon in flight is a normal state on the way
 * to a normal outcome. A violent colour there would teach a creator to read a working summon as a
 * fault, which is the same mistake as a material degrading to a magenta surface (§13.4).
 *
 * It lives in its own module for ONE reason: `librarySpawn.ts` takes this as an injected factory so
 * that module — and every headless proof that drives it — stays free of `three`. Mount wires this
 * one; a test wires nothing and gets no placeholder, which is the honest degrade.
 *
 * `markOverlay` is the ONE definition of "this object is dev furniture" (E1/E3): off the VRAM
 * census and `allowOverride = false`, so no view mode repaints it and no budget counts it.
 */

/** One geometry and one material for every placeholder this session ever shows. */
const CAGE_GEO = new THREE.EdgesGeometry(new THREE.BoxGeometry(1, 1, 1));
const CAGE_MAT = new THREE.LineBasicMaterial({ color: 0x7fd4ff, transparent: true, opacity: 0.7 });

export function createSummonPlaceholder(root: THREE.Object3D) {
  return (def: EntityPlacement): SpawnPlaceholder => {
    const cage = new THREE.LineSegments(CAGE_GEO, CAGE_MAT);
    const pos = def['pos'];
    if (Array.isArray(pos) && pos.length === 3 && pos.every((n) => typeof n === 'number')) {
      cage.position.set(pos[0] as number, pos[1] as number, pos[2] as number);
    }
    markOverlay(cage);
    root.add(cage);
    return {
      dispose() {
        cage.removeFromParent();
      },
    };
  };
}
